import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import {
  Sparkles, RefreshCw, TrendingUp, AlertTriangle, Lightbulb, ListChecks,
  Info, Loader2, Zap, Clock, Layers, Check, BellOff, RotateCcw, Trophy,
  PoundSterling,
} from "lucide-react";
import TabFreshnessNote from "./TabFreshnessNote";
import { MonthPicker, CompareMonthsPanel } from "./AIInsightsHistory";

/* ---------- visual helpers ---------- */

const SECTION_META = {
  wins: {
    icon: TrendingUp,
    label: "Wins",
    accent: "text-[#048056]",
    bg: "bg-[#f0fdf4]",
    border: "border-[#86efac]",
    chipBg: "bg-[#dcfce7]",
    chipText: "text-[#166534]",
  },
  concerns: {
    icon: AlertTriangle,
    label: "Concerns",
    accent: "text-[#c53030]",
    bg: "bg-[#fff1f2]",
    border: "border-[#fda4af]",
    chipBg: "bg-[#fee2e2]",
    chipText: "text-[#991b1b]",
  },
  opportunities: {
    icon: Lightbulb,
    label: "Opportunities",
    accent: "text-[#b45309]",
    bg: "bg-[#fffbeb]",
    border: "border-[#fcd34d]",
    chipBg: "bg-[#fef3c7]",
    chipText: "text-[#92400e]",
  },
};

const PRIORITY_STYLE = {
  high: "bg-[#fee2e2] text-[#991b1b] border-[#fca5a5]",
  medium: "bg-[#fef3c7] text-[#92400e] border-[#fcd34d]",
  low: "bg-[#e0f2fe] text-[#075985] border-[#7dd3fc]",
};

const EFFORT_STYLE = {
  quick: { label: "Quick win", icon: Zap, bg: "bg-[#dcfce7]", text: "text-[#166534]" },
  medium: { label: "Medium effort", icon: Clock, bg: "bg-[#fef3c7]", text: "text-[#92400e]" },
  deep: { label: "Deep work", icon: Layers, bg: "bg-[#e0e7ff]", text: "text-[#3730a3]" },
};

function formatPeriod(period) {
  if (!period) return "";
  const [y, m] = period.split("-");
  const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  return d.toLocaleString(undefined, { month: "long", year: "numeric" });
}

function formatGeneratedAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit",
  });
}

/**
 * Next scheduled auto-refresh — the monthly cron fires at 09:00 UTC on the
 * 1st of every month. Returns a Date object in the user's local timezone.
 */
function nextAutoRefreshAt() {
  const now = new Date();
  // First of NEXT month at 09:00 UTC (Date.UTC returns ms).
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 9, 0, 0));
}

function formatRelativeFuture(date) {
  const diffMs = date.getTime() - Date.now();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (days >= 1) return `in ${days}d`;
  if (hours >= 1) return `in ${hours}h`;
  const minutes = Math.max(1, Math.floor(diffMs / (1000 * 60)));
  return `in ${minutes}m`;
}

function formatRelativePast(iso) {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

/* ---------- sub-components ---------- */

function SectionBlock({ kind, items }) {
  const meta = SECTION_META[kind];
  const Icon = meta.icon;
  return (
    <div className="card-soft p-5" data-testid={`ai-section-${kind}`}>
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${meta.bg} ${meta.border} border`}>
          <Icon className={`h-3.5 w-3.5 ${meta.accent}`} strokeWidth={2.4} />
        </div>
        <h4 className="text-[11px] uppercase tracking-widest text-[#4a5568] font-bold">{meta.label}</h4>
        <span className="text-[10px] font-mono-tight text-[#a0aec0] ml-1">{items?.length || 0}</span>
      </div>
      {items?.length ? (
        <ul className="space-y-3">
          {items.map((it, i) => (
            <li key={i} className="text-sm" data-testid={`ai-${kind}-item-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="font-semibold text-[#1a202c] flex-1">{it.title}</div>
                {it.metric && (
                  <span
                    className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono-tight font-semibold ${meta.chipBg} ${meta.chipText}`}
                  >
                    {it.metric}
                  </span>
                )}
              </div>
              {it.detail && (
                <div className="text-[13px] text-[#4a5568] mt-1 leading-relaxed">{it.detail}</div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-[12px] text-[#a0aec0] italic">Nothing meaningful flagged this month.</div>
      )}
    </div>
  );
}

function ActionsBlock({ actions, onMark, marking }) {
  if (!actions?.length) {
    return (
      <div className="card-soft p-5" data-testid="ai-section-actions">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-[#ecfeff] border border-[#67e8f9]">
            <ListChecks className="h-3.5 w-3.5 text-[#0e7490]" strokeWidth={2.4} />
          </div>
          <h4 className="text-[11px] uppercase tracking-widest text-[#4a5568] font-bold">Recommended actions</h4>
        </div>
        <div className="text-[12px] text-[#a0aec0] italic">No specific actions this month.</div>
      </div>
    );
  }
  // Pending = visible up top, done/snoozed get a faded treatment further down
  const pending = actions.filter((a) => !a.state);
  const handled = actions.filter((a) => a.state);
  return (
    <div className="card-soft p-5" data-testid="ai-section-actions">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-[#ecfeff] border border-[#67e8f9]">
          <ListChecks className="h-3.5 w-3.5 text-[#0e7490]" strokeWidth={2.4} />
        </div>
        <h4 className="text-[11px] uppercase tracking-widest text-[#4a5568] font-bold">Recommended actions</h4>
        <span className="text-[10px] font-mono-tight text-[#a0aec0] ml-1">
          {pending.length} active{handled.length > 0 ? ` · ${handled.length} handled` : ""}
        </span>
      </div>
      <ol className="space-y-3">
        {pending.map((a, i) => (
          <ActionItem key={a.action_key || i} action={a} index={i} onMark={onMark} marking={marking} />
        ))}
        {handled.length > 0 && pending.length > 0 && (
          <li className="pt-2 pb-1">
            <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold border-t border-[#edf2f7] pt-3">
              Handled
            </div>
          </li>
        )}
        {handled.map((a, i) => (
          <ActionItem key={a.action_key || `h-${i}`} action={a} index={pending.length + i} onMark={onMark} marking={marking} faded />
        ))}
      </ol>
    </div>
  );
}

function ActionItem({ action, index, onMark, marking, faded }) {
  const priority = (action.priority || "medium").toLowerCase();
  const effort = (action.effort || "medium").toLowerCase();
  const effortMeta = EFFORT_STYLE[effort] || EFFORT_STYLE.medium;
  const EffortIcon = effortMeta.icon;
  const state = action.state;  // "done" | "snoozed" | undefined
  const isMarking = marking === action.action_key;
  const isDone = state === "done";
  const isSnoozed = state === "snoozed";
  const baseFade = faded || isDone || isSnoozed;

  const handle = (next) => () => {
    if (!action.action_key || isMarking) return;
    onMark(action, next);
  };

  return (
    <li
      className={`rounded-xl border bg-white p-3.5 transition-colors ${
        isDone ? "border-[#86efac] bg-[#f0fdf4]" : isSnoozed ? "border-[#fcd34d] bg-[#fffbeb]" : "border-[#edf2f7] hover:border-[#cbd5e0]"
      } ${baseFade ? "opacity-80" : ""}`}
      data-testid={`ai-action-item-${index}`}
      data-action-state={state || "pending"}
    >
      <div className="flex items-start gap-3">
        <div className={`shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold font-mono-tight ${
          isDone ? "bg-[#dcfce7] text-[#166534]" : isSnoozed ? "bg-[#fef3c7] text-[#92400e]" : "bg-[#f7fafc] text-[#4a5568] border border-[#edf2f7]"
        }`}>
          {isDone ? <Check className="h-3 w-3" strokeWidth={3} /> : isSnoozed ? <BellOff className="h-3 w-3" strokeWidth={2.6} /> : index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className={`text-sm font-semibold text-[#1a202c] ${isDone ? "line-through opacity-70" : ""}`}>
              {action.title}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {!isDone && !isSnoozed && (
                <>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] uppercase tracking-widest font-bold border ${PRIORITY_STYLE[priority] || PRIORITY_STYLE.medium}`}>
                    {priority}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${effortMeta.bg} ${effortMeta.text}`}>
                    <EffortIcon className="h-3 w-3" strokeWidth={2.4} /> {effortMeta.label}
                  </span>
                </>
              )}
              {isDone && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-widest font-bold bg-[#dcfce7] text-[#166534]">
                  <Check className="h-3 w-3" strokeWidth={3} /> Done
                </span>
              )}
              {isSnoozed && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-widest font-bold bg-[#fef3c7] text-[#92400e]">
                  <BellOff className="h-3 w-3" strokeWidth={2.6} /> Snoozed
                </span>
              )}
            </div>
          </div>
          {action.detail && (
            <div className={`text-[13px] text-[#4a5568] mt-1.5 leading-relaxed ${isDone ? "opacity-70" : ""}`}>{action.detail}</div>
          )}
          {/* Toggle row */}
          <div className="flex items-center gap-1.5 mt-3 flex-wrap" data-testid={`ai-action-toggles-${index}`}>
            {!state && (
              <>
                <button
                  type="button"
                  onClick={handle("done")}
                  disabled={isMarking}
                  data-testid={`ai-action-done-${index}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-[#86efac] bg-[#f0fdf4] text-[#166534] hover:bg-[#dcfce7] transition-colors disabled:opacity-50"
                >
                  <Check className="h-3 w-3" strokeWidth={2.6} /> Mark done
                </button>
                <button
                  type="button"
                  onClick={handle("snoozed")}
                  disabled={isMarking}
                  data-testid={`ai-action-snooze-${index}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-[#fcd34d] bg-[#fffbeb] text-[#92400e] hover:bg-[#fef3c7] transition-colors disabled:opacity-50"
                >
                  <BellOff className="h-3 w-3" strokeWidth={2.6} /> Snooze this month
                </button>
              </>
            )}
            {state && (
              <>
                <button
                  type="button"
                  onClick={handle(null)}
                  disabled={isMarking}
                  data-testid={`ai-action-undo-${index}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-[#e2e8f0] bg-white text-[#4a5568] hover:border-[#cbd5e0] transition-colors disabled:opacity-50"
                >
                  {isMarking ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" strokeWidth={2.6} />}
                  Undo
                </button>
                {action.marked_at && (
                  <span className="text-[10px] text-[#a0aec0] font-mono-tight">
                    {new Date(action.marked_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                  </span>
                )}
              </>
            )}
            {isMarking && !state && <Loader2 className="h-3 w-3 animate-spin text-[#a0aec0]" />}
          </div>
        </div>
      </div>
    </li>
  );
}

function CompletedRollupBlock({ items, periodLabel }) {
  if (!items?.length) return null;
  return (
    <div className="card-soft p-5" data-testid="ai-completed-rollup">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-[#dcfce7] border border-[#86efac]">
          <Trophy className="h-3.5 w-3.5 text-[#166534]" strokeWidth={2.4} />
        </div>
        <h4 className="text-[11px] uppercase tracking-widest text-[#166534] font-bold">Completed{periodLabel ? ` · ${periodLabel}` : ""}</h4>
        <span className="text-[10px] font-mono-tight text-[#a0aec0] ml-1">{items.length}</span>
      </div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li
            key={it.action_key || i}
            className="flex items-start gap-3 px-3 py-2 rounded-lg bg-[#f0fdf4] border border-[#86efac]/40"
            data-testid={`ai-completed-item-${i}`}
          >
            <div className="shrink-0 h-5 w-5 rounded-full bg-[#dcfce7] text-[#166534] flex items-center justify-center">
              <Check className="h-3 w-3" strokeWidth={3} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-[#1a202c] line-through opacity-80">{it.title || "(untitled action)"}</div>
              <div className="text-[10px] text-[#a0aec0] font-mono-tight mt-0.5">
                {it.marked_at ? new Date(it.marked_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : ""}
                {it.priority && <span> · {it.priority} priority</span>}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- main tab ---------- */

function TrafficValueNarrative({ traffic, clientName }) {
  const gbp = Math.round(traffic.gbp_per_month);
  const gbpYear = Math.round(traffic.gbp_per_year);
  const top = traffic.top_contributors?.[0];
  return (
    <div
      className="card-soft p-5 bg-gradient-to-br from-[#f0fdfa] to-white border border-[#4fd1c5]/25"
      data-testid="ai-insights-seo-vs-ppc"
    >
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 rounded-lg bg-[#4fd1c5]/20 text-[#2c7a7b] flex items-center justify-center shrink-0">
          <PoundSterling className="h-4 w-4" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#2c7a7b] font-bold">SEO vs paid ads · what you're really saving</div>
          <p className="mt-1 text-[13px] leading-relaxed text-[#1a202c]">
            If {clientName || "you"} had to buy this same visibility through Google Ads,
            it would cost roughly <strong className="text-[#2c7a7b]">£{gbp.toLocaleString()} per month</strong>
            {" "}(<span className="font-mono-tight text-[#4a5568]">£{gbpYear.toLocaleString()}/year</span>) at current cost-per-click rates.
            {top && (
              <> The single biggest contributor is "<span className="font-mono-tight text-[#4a5568]">{top.keyword}</span>" at £{Math.round(top.monthly_gbp).toLocaleString()}/month.</>
            )}
            {" "}That's the compounding value of your SEO investment — traffic that keeps arriving without a monthly ad spend attached.
          </p>
        </div>
      </div>
    </div>
  );
}


export default function AIInsightsTab({ client }) {
  const { isAdmin } = useAuth();
  const [state, setState] = useState({ insight: null, stale: true, period: null });
  const [loading, setLoading] = useState(true);   // initial fetch
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [marking, setMarking] = useState(null);  // action_key currently being toggled
  // Guard against React Strict Mode double-invocation of the auto-generate effect
  const autoTriggered = useRef(false);
  const [periods, setPeriods] = useState([]);
  const [viewPeriod, setViewPeriod] = useState(null);  // null = latest
  const [pastDoc, setPastDoc] = useState(null);
  const [pastLoading, setPastLoading] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${client.id}/ai-insights/history`);
      setPeriods(data.periods || []);
    } catch {
      setPeriods([]);
    }
  }, [client.id]);

  const loadPast = useCallback(async (period) => {
    setPastLoading(true);
    try {
      const { data } = await api.get(`/clients/${client.id}/ai-insights?period=${period}`);
      setPastDoc(data.insight);
      await fetchCompleted(period);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load that month");
    } finally {
      setPastLoading(false);
    }
  }, [client.id]);

  const changeMonth = (period) => {
    setViewPeriod(period);
    if (period) loadPast(period);
    else {
      setPastDoc(null);
      fetchCompleted(state.insight?.period);
    }
  };

  const fetchCompleted = useCallback(async (period) => {
    try {
      const q = period ? `?period=${period}` : "";
      const { data } = await api.get(`/clients/${client.id}/ai-insights/completed${q}`);
      setCompleted(data.items || []);
    } catch {
      setCompleted([]);
    }
  }, [client.id]);

  const runGenerate = async (showToast = true) => {
    setGenerating(true);
    setError(null);
    try {
      const { data } = await api.post(`/clients/${client.id}/ai-insights/generate`);
      setState({
        insight: data.insight,
        stale: !!data.stale,
        period: data.current_period,
      });
      // Refresh completed list for the same period the briefing belongs to
      await fetchCompleted(data.insight?.period);
      fetchHistory();
      setViewPeriod(null);
      setPastDoc(null);
      if (showToast) toast.success("AI insights refreshed.");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Failed to generate insights";
      setError(detail);
      if (showToast) toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  // Toggle an action's state. Optimistically updates UI, rolls back on error.
  const handleMark = useCallback(async (action, nextStatus) => {
    if (!action?.action_key) return;
    setMarking(action.action_key);
    const prevActions = state.insight?.insight?.recommended_actions || [];
    // Optimistic update
    setState((s) => {
      if (!s.insight) return s;
      const next = { ...s.insight, insight: { ...s.insight.insight } };
      next.insight.recommended_actions = (next.insight.recommended_actions || []).map((a) =>
        a.action_key === action.action_key
          ? { ...a, state: nextStatus || undefined, marked_at: nextStatus ? new Date().toISOString() : null }
          : a,
      );
      return { ...s, insight: next };
    });
    try {
      await api.post(
        `/clients/${client.id}/ai-insights/actions/${action.action_key}/mark`,
        {
          status: nextStatus,
          action: nextStatus
            ? {
                title: action.title,
                detail: action.detail,
                priority: action.priority,
                effort: action.effort,
                source_period: state.insight?.period,
              }
            : null,
        },
      );
      if (nextStatus === "done") toast.success("Marked done.");
      else if (nextStatus === "snoozed") toast.success("Snoozed for this month.");
      else toast.success("Action restored.");
      // Refresh completed rollup so the change is reflected
      fetchCompleted(state.insight?.period);
    } catch (e) {
      // Roll back on failure
      setState((s) => {
        if (!s.insight) return s;
        const next = { ...s.insight, insight: { ...s.insight.insight, recommended_actions: prevActions } };
        return { ...s, insight: next };
      });
      toast.error(e?.response?.data?.detail || "Couldn't update action");
    } finally {
      setMarking(null);
    }
  }, [client.id, state.insight, fetchCompleted]);

  // Initial load — fetch cached, then auto-generate if stale (no cached insight for current month)
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/clients/${client.id}/ai-insights`);
        if (!alive) return;
        setState({
          insight: data.insight,
          stale: !!data.stale,
          period: data.current_period,
        });
        // Fetch completed rollup for the current period
        await fetchCompleted(data.insight?.period || data.current_period);
        fetchHistory();
        // Auto-generate if no insight cached for this month yet — admin only,
        // since members can't trigger the underlying POST (it 403s server-side)
        if (isAdmin && data.stale && !client.frozen && !autoTriggered.current) {
          autoTriggered.current = true;
          // small delay so the "Generating…" state is visible to the user
          setTimeout(() => alive && runGenerate(false), 50);
        }
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Couldn't load insights");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [client.id]);

  const latestDoc = state.insight;
  const insightDoc = viewPeriod ? pastDoc : latestDoc;
  const ai = insightDoc?.insight || null;
  const isCurrentMonth = viewPeriod ? true : !state.stale;
  const viewingPast = !!viewPeriod;

  // Past months: mark via the API, then reload that month so its done states refresh.
  const markPast = async (action, nextStatus) => {
    if (!action?.action_key) return;
    setMarking(action.action_key);
    try {
      await api.post(`/clients/${client.id}/ai-insights/actions/${action.action_key}/mark`, {
        status: nextStatus,
        action: nextStatus ? { title: action.title, detail: action.detail, priority: action.priority, effort: action.effort, source_period: viewPeriod } : null,
      });
      await loadPast(viewPeriod);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update action");
    } finally {
      setMarking(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="ai-insights-tab">
      <TabFreshnessNote testid="ai-insights-freshness">
        Auto-generated monthly on the 1st (after the digest send) · Tap Regenerate for an on-demand refresh.
      </TabFreshnessNote>

      {/* Header */}
      <div className="card-soft p-5 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#4fd1c5] to-[#319795] text-white flex items-center justify-center shrink-0 shadow-[0_4px_12px_-2px_rgba(56,178,172,0.4)]">
            <Sparkles className="h-5 w-5" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">AI Insights</div>
            <h3 className="font-display text-2xl mt-0.5 text-[#1a202c]">
              {insightDoc ? formatPeriod(insightDoc.period) : formatPeriod(state.period)} briefing
            </h3>
            <p className="text-xs text-[#718096] mt-1">
              Generated by Claude Opus reading your rank, Search Console, GA4 and competitor data.
            </p>
            {/* Freshness chips — Last generated + Next auto-refresh */}
            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="ai-insights-freshness-chips">
              {insightDoc?.generated_at ? (
                <span
                  data-testid="ai-insights-last-generated"
                  title={`Last generated at ${formatGeneratedAt(insightDoc.generated_at)}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#4fd1c5]/10 border border-[#4fd1c5]/30 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#234e52]"
                >
                  <Clock className="h-3 w-3" strokeWidth={2.4} />
                  Last generated <span className="font-mono-tight normal-case tracking-normal text-[#2c7a7b]">{formatRelativePast(insightDoc.generated_at)}</span>
                </span>
              ) : (
                <span
                  data-testid="ai-insights-last-generated-none"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#edf2f7] border border-[#e2e8f0] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#718096]"
                >
                  <Clock className="h-3 w-3" strokeWidth={2.4} />
                  Not generated yet
                </span>
              )}
              {(() => {
                const nextAt = nextAutoRefreshAt();
                return (
                  <span
                    data-testid="ai-insights-next-refresh"
                    title={`Auto-regenerates on the 1st of every month at 09:00 UTC — next run at ${formatGeneratedAt(nextAt.toISOString())}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#f7fafc] border border-[#e2e8f0] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#4a5568]"
                  >
                    <RefreshCw className="h-3 w-3" strokeWidth={2.4} />
                    Next auto-refresh <span className="font-mono-tight normal-case tracking-normal text-[#1a202c]">{formatRelativeFuture(nextAt)}</span>
                  </span>
                );
              })()}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <MonthPicker
          periods={periods}
          value={viewPeriod}
          latestPeriod={latestDoc?.period}
          onChange={changeMonth}
          onCompare={() => setCompareOpen((o) => !o)}
        />
        {isAdmin && !viewingPast && (
          <button
            onClick={() => runGenerate(true)}
            disabled={generating || loading || client.frozen}
            title={client.frozen ? "Client is frozen — unfreeze to pull new data" : undefined}
            data-testid="ai-insights-regenerate"
            className="btn-primary inline-flex items-center gap-2"
          >
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {generating ? "Generating…" : isCurrentMonth ? "Regenerate" : "Generate now"}
          </button>
        )}
        </div>
      </div>

      {compareOpen && periods.length > 1 && (
        <CompareMonthsPanel clientId={client.id} periods={periods} onClose={() => setCompareOpen(false)} />
      )}

      {viewingPast && (
        <div className="card-soft px-5 py-3 flex items-center justify-between gap-3 text-xs bg-[#f7fafc] border border-[#e2e8f0]" data-testid="ai-insights-past-banner">
          <span className="text-[#4a5568]">
            <Clock className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5 text-[#a0aec0]" />
            Viewing the saved <strong>{formatPeriod(viewPeriod)}</strong> briefing — exactly as it was generated.
          </span>
          <button type="button" onClick={() => changeMonth(null)} className="font-semibold text-[#319795] hover:underline" data-testid="ai-insights-back-to-latest">
            Back to latest
          </button>
        </div>
      )}

      {pastLoading && (
        <div className="card-soft p-10 text-center" data-testid="ai-insights-past-loading">
          <Loader2 className="h-5 w-5 animate-spin mx-auto text-[#4fd1c5]" />
        </div>
      )}

      {/* Loading / Generating / Error / Content */}
      {loading && (
        <div className="card-soft p-10 text-center" data-testid="ai-insights-loading">
          <Loader2 className="h-5 w-5 animate-spin mx-auto text-[#4fd1c5]" />
          <div className="text-xs text-[#a0aec0] mt-2 font-mono-tight">Loading cached insights…</div>
        </div>
      )}

      {!loading && generating && !ai && (
        <div className="card-soft p-10 text-center" data-testid="ai-insights-generating">
          <div className="h-12 w-12 mx-auto rounded-2xl bg-gradient-to-br from-[#4fd1c5] to-[#319795] flex items-center justify-center mb-4 shadow-[0_8px_20px_-4px_rgba(56,178,172,0.5)] animate-pulse">
            <Sparkles className="h-6 w-6 text-white" strokeWidth={2} />
          </div>
          <div className="text-sm font-semibold text-[#1a202c]">Generating your briefing…</div>
          <div className="text-xs text-[#a0aec0] mt-1.5 max-w-md mx-auto leading-relaxed">
            Claude Opus is reading your latest rank snapshots, Search Console + GA4 data, and competitor share-of-voice. This usually takes 15–30 seconds.
          </div>
        </div>
      )}

      {!loading && error && !ai && (
        <div className="card-soft p-6" data-testid="ai-insights-error">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-xl bg-[#fee2e2] border border-[#fca5a5] flex items-center justify-center shrink-0">
              <AlertTriangle className="h-4 w-4 text-[#c53030]" strokeWidth={2.4} />
            </div>
            <div>
              <div className="text-sm font-semibold text-[#c53030]">Couldn&apos;t generate insights</div>
              <div className="text-[13px] text-[#4a5568] mt-1 font-mono-tight">{error}</div>
              <button
                onClick={() => runGenerate(true)}
                className="btn-ghost mt-3 inline-flex items-center gap-2 text-xs"
                data-testid="ai-insights-retry"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {ai && !pastLoading && (
        <>
          {/* Headline + summary card */}
          <div className="card-soft p-6 bg-gradient-to-br from-[#f7fafc] to-white border border-[#4fd1c5]/20" data-testid="ai-insights-headline">
            <div className="text-[10px] uppercase tracking-widest text-[#4fd1c5] font-bold mb-2">Headline</div>
            <div className="font-display text-xl text-[#1a202c] leading-snug">{ai.headline}</div>
            {ai.summary && (
              <div className="text-[14px] text-[#4a5568] mt-3 leading-relaxed">{ai.summary}</div>
            )}
            {!isCurrentMonth && insightDoc?.period && (
              <div className="mt-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#fef3c7] border border-[#fcd34d] text-[10px] uppercase tracking-widest font-bold text-[#92400e]">
                <Info className="h-3 w-3" strokeWidth={2.6} />
                Showing {formatPeriod(insightDoc.period)} — regenerate for current month
              </div>
            )}
          </div>

          {/* Traffic value strip — "your rankings = £X/month in PPC equivalent".
              Renders only when we have a computable value; small caveat pill
              when only a fraction of tracked keywords have full data. */}
          {insightDoc?.traffic_value && insightDoc.traffic_value.usd_per_month > 0 && (
            <div
              className="card-soft p-5 bg-gradient-to-br from-[#f0fdfa] to-white border border-[#4fd1c5]/25"
              data-testid="ai-insights-traffic-value"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-xl bg-[#4fd1c5]/20 text-[#2c7a7b] flex items-center justify-center shrink-0">
                    <PoundSterling className="h-5 w-5" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#2c7a7b] font-bold">Estimated PPC value of current rankings</div>
                    <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                      <div className="font-display text-3xl text-[#1a202c]">
                        £{insightDoc.traffic_value.gbp_per_month.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                      <div className="text-sm text-[#718096]">per month</div>
                      <div className="text-[11px] text-[#a0aec0] font-mono-tight">
                        ≈ ${insightDoc.traffic_value.usd_per_month.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        {" · £"}{insightDoc.traffic_value.gbp_per_year.toLocaleString(undefined, { maximumFractionDigits: 0 })}/year
                      </div>
                    </div>
                    <div className="text-[11px] text-[#718096] mt-1.5">
                      {insightDoc.traffic_value.kw_top_10} top-10 · {insightDoc.traffic_value.kw_with_data} of {insightDoc.traffic_value.keyword_count} keywords with volume + CPC data
                    </div>
                  </div>
                </div>
                {insightDoc.traffic_value.top_contributors?.length > 0 && (
                  <div className="text-[11px] text-[#4a5568] max-w-xs">
                    <div className="uppercase tracking-widest text-[#a0aec0] font-bold text-[10px] mb-1">Top contributors</div>
                    <ul className="space-y-0.5">
                      {insightDoc.traffic_value.top_contributors.slice(0, 3).map((k, i) => (
                        <li key={i} className="flex items-center justify-between gap-2">
                          <span className="truncate font-mono-tight">{k.keyword}</span>
                          <span className="text-[#2c7a7b] font-semibold tabular-nums shrink-0">£{Math.round(k.monthly_gbp).toLocaleString()}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {insightDoc.traffic_value.coverage_note && (
                <div className="text-[10px] text-[#a0aec0] mt-3 inline-flex items-center gap-1">
                  <Info className="h-3 w-3" /> {insightDoc.traffic_value.coverage_note}
                </div>
              )}
            </div>
          )}

          {/* 3-column wins / concerns / opportunities */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <SectionBlock kind="wins" items={ai.wins} />
            <SectionBlock kind="concerns" items={ai.concerns} />
            <SectionBlock kind="opportunities" items={ai.opportunities} />
          </div>

          {/* SEO-vs-PPC narrative — compact plain-english framing of the
              traffic-value figure. Mirrors the same box on the PDF cover so
              account managers see the same wording that lands with the
              client. Only renders when we have a computable value. */}
          {insightDoc?.traffic_value && insightDoc.traffic_value.usd_per_month > 0 && (
            <TrafficValueNarrative traffic={insightDoc.traffic_value} clientName={client.name} />
          )}

          {/* Recommended actions */}
          <ActionsBlock actions={ai.recommended_actions} onMark={viewingPast ? markPast : handleMark} marking={marking} />

          {/* Completed rollup — surfaces only when at least one action has been marked done */}
          <CompletedRollupBlock
            items={completed}
            periodLabel={insightDoc?.period ? formatPeriod(insightDoc.period) : ""}
          />

          {/* Data caveats — only show when Claude flagged sparse data */}
          {ai.data_caveats && ai.data_caveats.trim() && (
            <div
              className="card-soft p-4 bg-[#fef3c7]/40 border border-[#fcd34d]/40"
              data-testid="ai-insights-caveats"
            >
              <div className="flex items-start gap-2.5">
                <Info className="h-4 w-4 text-[#b45309] mt-0.5 shrink-0" strokeWidth={2.4} />
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-[#92400e] font-bold">Data note</div>
                  <div className="text-[13px] text-[#78350f] mt-1 leading-relaxed">{ai.data_caveats}</div>
                </div>
              </div>
            </div>
          )}

          {/* Footer attribution */}
          <div className="text-[10px] text-[#a0aec0] font-mono-tight text-center pt-1" data-testid="ai-insights-attribution">
            Model: {insightDoc.model || "anthropic/claude-opus-4-8"} · Auto-regenerated on the 1st of each month
          </div>
        </>
      )}
    </div>
  );
}
